import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ensureValidToken } from "./authRefresh";

export interface User {
  username: string;
  storeName: string;
  free_shipping_value: number;
  default_shipping: number;
  default_weight: number;
  access_scopes: [string];
}

export interface AuthState {
  user: User | null;
  authToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  isAuthorized: boolean | string;
}

const initialState: AuthState = {
  user: localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user")!)
    : null,
  authToken: null,
  refreshToken: localStorage.getItem("refresh_token") || null,
  isAuthorized: localStorage.getItem("auth_token") ? true : false,
  loading: false,
  error: null,
};

export const login = createAsyncThunk(
  "auth/userData",
  async (credentials: { token: string }, { rejectWithValue }) => {
    try {
      let token = credentials?.token || localStorage.getItem("auth_token");
      let response = await fetch("/api/v1/me", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      });

      // Handle 401 - use shared refresh (same as BaseQuery) so only one POST /api/v1/refresh-token runs
      if (response.status === 401) {
        const refreshToken = localStorage.getItem("refresh_token");
        if (refreshToken) {
          try {
            await ensureValidToken();
            token = localStorage.getItem("auth_token") || token;
            response = await fetch("/api/v1/me", {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                authorization: `Bearer ${token}`,
              },
            });
            if (response.status === 401) {
              localStorage.removeItem("auth_token");
              localStorage.removeItem("refresh_token");
              localStorage.removeItem("user");
              return rejectWithValue("Session expired. Please login again.");
            }
          } catch {
            localStorage.removeItem("auth_token");
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("user");
            return rejectWithValue("Session expired. Please login again.");
          }
        } else {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("user");
          return rejectWithValue("Unauthorized");
        }
      }

      if (!response.ok) {
        throw new Error("Failed to log in");
      }
      const data = await response.json();
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to log in");
    }
  }
);

// Updated logout action: hitting the API without waiting for the response
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      // Hit the logout API route but don't wait for the response
      const authToken = localStorage.getItem("auth_token");
      const refreshToken = localStorage.getItem("refresh_token");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: authToken ? `Bearer ${authToken}` : "",
      };
      if (refreshToken) {
        headers["x-refresh-token"] = refreshToken;
      }
      fetch("/api/v1/logout", {
        method: "POST",
        headers,
      }).catch((error) => console.error("Logout API error:", error));
      // Reset the state after hitting the logout route
      state.user = null;
      state.authToken = null;
      state.refreshToken = null;
      state.isAuthorized = false;
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Don't set isAuthorized to false on pending if we have a token
        // This prevents premature redirects during token refresh
        if (!localStorage.getItem("auth_token")) {
          state.isAuthorized = false;
        }
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthorized = true;
        state.user = action.payload.data;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthorized = false;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
